/** The page's height, set on the root element; the page falls back to `100dvh` until it is. */
export const APP_HEIGHT_VAR = "--app-height";

const isEditing = (): boolean => {
  const element = document.activeElement;
  return (
    element instanceof HTMLElement &&
    (element.isContentEditable || element.tagName === "INPUT" || element.tagName === "TEXTAREA")
  );
};

/**
 * Keeps the page at its full height while the on-screen keyboard is open. Safari on iOS lays the keyboard
 * over the page; Chrome on iOS shrinks the viewport to the space above it, which squashes the whole
 * layout until the prompt editor has no room to show what is being typed. So on a touch device the page's
 * height follows the viewport except when it shrinks, at the same width, while a text field has focus:
 * then it stays put, as it does in Safari, and the browser scrolls the field into view. Returns a cleanup.
 */
export function trackKeyboardSafeHeight(): () => void {
  if (typeof window === "undefined" || !window.matchMedia?.("(pointer: coarse)").matches) return () => {};
  const root = document.documentElement;
  let width = window.innerWidth;
  let height = window.innerHeight;
  root.style.setProperty(APP_HEIGHT_VAR, `${height}px`);
  const onResize = () => {
    const nextWidth = window.innerWidth;
    const nextHeight = window.innerHeight;
    if (nextWidth === width && nextHeight < height && isEditing()) return;
    width = nextWidth;
    height = nextHeight;
    root.style.setProperty(APP_HEIGHT_VAR, `${height}px`);
  };
  window.addEventListener("resize", onResize);
  return () => {
    window.removeEventListener("resize", onResize);
    root.style.removeProperty(APP_HEIGHT_VAR);
  };
}
